import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useCodes } from "../lib/CodesContext";
import { DEFAULT_CODES } from "../lib/site";

const ADMIN_PASSWORD = "501823";

const AdminModal = () => {
  const { adminOpen, setAdminOpen, codes, saveCodes, resetCodes } = useCodes();
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [error, setError] = useState(false);
  const [downloader, setDownloader] = useState("");
  const [ntdown, setNtdown] = useState("");

  const close = () => {
    setAdminOpen(false);
    setPassword("");
    setAuthed(false);
    setError(false);
  };

  const tryAuth = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setAuthed(true);
      setDownloader(codes.downloader);
      setNtdown(codes.ntdown);
      setError(false);
    } else {
      setError(true);
      toast.error("Senha incorreta");
    }
  };

  const save = (e) => {
    e.preventDefault();
    if (!downloader.trim() || !ntdown.trim()) {
      toast.error("Preencha os dois códigos");
      return;
    }
    saveCodes({ downloader: downloader.trim(), ntdown: ntdown.trim() });
    toast.success("Códigos atualizados com sucesso");
    close();
  };

  const restore = () => {
    resetCodes();
    setDownloader(DEFAULT_CODES.downloader);
    setNtdown(DEFAULT_CODES.ntdown);
    toast.success("Códigos restaurados para o padrão");
  };

  return (
    <AnimatePresence>
      {adminOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          onClick={close}
          data-testid="admin-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-lime-350/20 bg-ink-900 p-8 shadow-2xl"
            data-testid="admin-modal"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-lime-350/10 text-lime-350">
                  <Lock className="h-4 w-4" />
                </span>
                <h2 className="font-display text-2xl uppercase tracking-wider text-slate-100">
                  Painel Admin
                </h2>
              </div>
              <button
                onClick={close}
                data-testid="admin-close-button"
                aria-label="Fechar"
                className="rounded-full p-2 text-slate-500 transition-colors hover:text-slate-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {!authed ? (
              <form onSubmit={tryAuth} className="mt-6">
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                  Senha de acesso
                </label>
                <motion.input
                  key={error ? "err" : "ok"}
                  initial={error ? { x: -8 } : false}
                  animate={error ? { x: [0, -8, 8, -4, 4, 0] } : {}}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-password-input"
                  placeholder="••••••"
                  autoFocus
                  className={`mt-2 w-full rounded-xl border bg-ink-950 px-4 py-3.5 font-mono text-lg tracking-[0.3em] text-slate-100 outline-none transition-colors focus:border-lime-350 ${
                    error ? "border-red-500/60" : "border-white/10"
                  }`}
                />
                <button
                  type="submit"
                  data-testid="admin-login-button"
                  className="mt-4 w-full rounded-xl bg-lime-350 py-3.5 text-sm font-bold uppercase tracking-wider text-ink-950 transition-transform hover:-translate-y-0.5 active:scale-95"
                >
                  Entrar
                </button>
              </form>
            ) : (
              <form onSubmit={save} className="mt-6 space-y-5">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-orange-400">
                    Código Downloader
                  </label>
                  <input
                    value={downloader}
                    onChange={(e) => setDownloader(e.target.value)}
                    data-testid="admin-downloader-input"
                    className="mt-2 w-full rounded-xl border border-white/10 bg-ink-950 px-4 py-3.5 font-mono text-lg tracking-[0.2em] text-slate-100 outline-none focus:border-orange-400"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-lime-350">
                    Código NtDown
                  </label>
                  <input
                    value={ntdown}
                    onChange={(e) => setNtdown(e.target.value)}
                    data-testid="admin-ntdown-input"
                    className="mt-2 w-full rounded-xl border border-white/10 bg-ink-950 px-4 py-3.5 font-mono text-lg tracking-[0.2em] text-slate-100 outline-none focus:border-lime-350"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="submit"
                    data-testid="admin-save-button"
                    className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-lime-350 py-3.5 text-sm font-bold uppercase tracking-wider text-ink-950 transition-transform hover:-translate-y-0.5 active:scale-95"
                  >
                    <Save className="h-4 w-4" />
                    Salvar
                  </button>
                  <button
                    type="button"
                    onClick={restore}
                    data-testid="admin-restore-button"
                    className="flex items-center justify-center gap-2 rounded-xl border border-white/10 px-4 py-3.5 text-sm font-semibold text-slate-400 transition-colors hover:border-lime-350/40 hover:text-lime-350"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Padrão
                  </button>
                </div>
                <p className="text-center text-[11px] leading-relaxed text-slate-600">
                  Os códigos ficam salvos neste navegador e passam a valer
                  imediatamente na página de Download.
                </p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AdminModal;
